import React, { useState } from 'react';
import { Music2, Globe, Check } from 'lucide-react';
import { Artwork, Language } from '../types';
import {
  getArtworkArtist,
  getArtworkDescription,
  getArtworkTitle,
} from '../utils/artworkContent';

interface ArtworkDisplayProps {
  artwork: Artwork;
  selectedLanguage: Language;
  onLanguageChange: (language: Language) => void;
}

const languages: { id: Language; label: string; native: string }[] = [
  { id: 'english', label: 'English', native: 'English' },
  { id: 'hindi', label: 'Hindi', native: 'हिन्दी' },
  { id: 'kannada', label: 'Kannada', native: 'ಕನ್ನಡ' }
];

export function ArtworkDisplay({
  artwork,
  selectedLanguage,
  onLanguageChange
}: ArtworkDisplayProps) {
  const [showDropdown, setShowDropdown] = useState(false);

  const title = getArtworkTitle(artwork, selectedLanguage);
  const artist = getArtworkArtist(artwork, selectedLanguage);
  const description = getArtworkDescription(artwork, selectedLanguage);

  const hasTitle = title;
  const mediumOfArtwork = artwork?.medium_of_artwork;
  const sizeInInches = artwork?.size_in_inches;
  const yearOfCreation = artwork?.year_of_creation;
  const hasArtist = artist?.trim();
  const hasImage = artwork.imageUrl?.trim();
  const hasDescription = description?.trim();

  const hasAnyContent =
    hasTitle || hasArtist || hasImage || hasDescription;

  // If nothing exists, show centered music icon
  if (!hasAnyContent) {
    return (
      <div className="min-h-screen pt-20 pb-28 px-4 flex items-center justify-center relative">
        
        {/* Language Selector */}
        <div className="absolute top-6 right-6 z-20">
          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 
                         flex items-center justify-center hover:bg-gallery-accent/10 transition"
            >
              <Globe className="w-5 h-5 text-gallery-accent" />
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
                {languages.map((lang) => {
                  const isSelected = selectedLanguage === lang.id;

                  return (
                    <button
                      key={lang.id}
                      onClick={() => {
                        onLanguageChange(lang.id);
                        setShowDropdown(false);
                      }}
                      className={`w-full px-4 py-3 flex items-center justify-between transition
                        ${
                          isSelected
                            ? 'bg-gallery-accent/10 text-gallery-accent'
                            : 'hover:bg-gray-50'
                        }`}
                    >
                      <div className="text-left">
                        <p className="font-medium">{lang.label}</p>
                        <p className="text-sm opacity-70">{lang.native}</p>
                      </div>

                      {isSelected && (
                        <Check className="w-4 h-4" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="text-center text-gallery-accent">
          <Music2
            className="w-28 h-28 lg:w-40 lg:h-40 mx-auto"
            strokeWidth={1.5}
          />
          <p className="mt-4 text-lg font-medium opacity-80">
            Audio Experience
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="pt-20 pb-28 px-4 max-w-3xl mx-auto min-h-screen relative">
      
      {/* Top Language Selector */}
      <div className="absolute top-20 right-4 lg:right-0 z-20">
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-8 md:w-12 h-8 md:h-12 rounded-full bg-white shadow-lg border border-gray-300 
                       flex items-center justify-center hover:bg-gallery-accent group transition-all duration-400 ease-in-out"
          >
            <Globe className="w-4 md:w-5 h-4 md:h-5 text-gallery-accent group-hover:text-white transition-all duration-400 ease-in-out" />
          </button>

          {showDropdown && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
              {languages.map((lang) => {
                const isSelected = selectedLanguage === lang.id;

                return (
                  <button
                    key={lang.id}
                    onClick={() => {
                      onLanguageChange(lang.id);
                      setShowDropdown(false);
                    }}
                    className={`w-full px-4 py-3 flex items-center justify-between transition
                      ${
                        isSelected
                          ? 'bg-gallery-accent/10 text-gallery-accent'
                          : 'hover:bg-gray-50'
                      }`}
                  >
                    <div className="text-left">
                      <p className="font-medium">{lang.label}</p>
                      <p className="text-sm opacity-70">{lang.native}</p>
                    </div>

                    {isSelected && (
                      <Check className="w-4 h-4" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-start flex-col lg:flex-row flex-wrap gap-4 lg:gap-10">
        
        {/* Title and Artist */}
        {(hasTitle || hasArtist) && (
          <div className="mb-6 lg:mb-0 w-full pr-14">
            {hasTitle && (
              <h1 className="text-2xl font-bold text-gallery-dark mb-1">
                {title}
              </h1>
            )}

            {hasArtist && (
              <p className="text-gallery-accent font-medium">
               {artist}
              </p>
              
            )}
             {mediumOfArtwork && (
              <p className="text-gallery-accent font-medium">
               {mediumOfArtwork}
              </p>
              
            )}
             {sizeInInches && (
              <p className="text-gallery-accent font-medium">
               {sizeInInches}
              </p>
              
            )}
             {yearOfCreation && (
              <p className="text-gallery-accent font-medium">
               {yearOfCreation}
              </p>
              
            )}
          </div>
        )}

        {/* Artwork Image */}
        {hasImage && (
          <div className="w-full lg:w-1/2 relative aspect-square rounded-xl overflow-hidden mb-6">
            <img
              src={artwork.imageUrl}
              alt={title || 'Artwork'}
              className="w-full h-full object-contain"
            />
          </div>
        )}

        {/* Description */}
        {hasDescription && (
          <div className="flex-1 min-h-0">
            <div className="description-scroll h-full max-h-48 lg:max-h-[23rem] overflow-y-auto pr-2">
              <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {description}
              </p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
